import { useEffect, useState } from "react";
import { fetchReviews, fetchReviewSummary, replyToReview } from "@/lib/api";
import { Star, Reply as ReplyIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function ReviewsBlock({ gigId, sellerId }) {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState(0); // 0 = all
  const [replyOpen, setReplyOpen] = useState({}); // review_id -> bool
  const [replyText, setReplyText] = useState({}); // review_id -> str

  useEffect(() => {
    if (!gigId) return;
    fetchReviewSummary({ gig_id: gigId }).then(setSummary).catch(() => {});
  }, [gigId]);

  const load = () => {
    if (!gigId) return;
    const params = { gig_id: gigId };
    if (filter) params.rating = filter;
    fetchReviews(params).then(setReviews).catch(() => setReviews([]));
  };
  useEffect(load, [gigId, filter]);

  const isSeller = user?.user_id === sellerId;

  const submitReply = async (review_id) => {
    const txt = (replyText[review_id] || "").trim();
    if (!txt) return;
    try {
      await replyToReview(review_id, txt);
      toast.success("Reply posted");
      setReplyOpen((s) => ({ ...s, [review_id]: false }));
      setReplyText((s) => ({ ...s, [review_id]: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const total = summary?.total || 0;
  return (
    <div className="space-y-6">
      {/* Summary + breakdown */}
      {summary && total > 0 && (
        <div className="border border-foreground/15 bg-card p-6 grid md:grid-cols-[220px_1fr] gap-8" data-testid="rating-summary">
          <div className="flex flex-col items-center justify-center border-r-0 md:border-r border-foreground/10 pr-0 md:pr-6">
            <div className="mono text-5xl font-bold" data-testid="rating-avg">{summary.avg.toFixed(1)}</div>
            <div className="flex gap-0.5 mt-2">
              {[1,2,3,4,5].map((n) => (
                <Star key={n} className={`w-4 h-4 ${n <= Math.round(summary.avg) ? "fill-primary text-primary" : "text-muted"}`}/>
              ))}
            </div>
            <div className="mono text-xs text-muted-foreground mt-2">{total} REVIEWS</div>
          </div>
          <div className="space-y-2">
            {[5,4,3,2,1].map((star) => {
              const count = summary.breakdown[String(star)] || 0;
              const pct = total ? Math.round((count / total) * 100) : 0;
              const active = filter === star;
              return (
                <button
                  key={star}
                  data-testid={`filter-rating-${star}`}
                  onClick={() => setFilter(active ? 0 : star)}
                  className={`w-full flex items-center gap-3 group ${active ? "opacity-100" : "opacity-90 hover:opacity-100"}`}
                >
                  <div className="flex items-center gap-1 w-10 text-xs mono">
                    {star}<Star className="w-3 h-3 fill-foreground text-foreground"/>
                  </div>
                  <div className="flex-1 h-2 bg-muted overflow-hidden">
                    <div className={`h-full ${active ? "bg-primary" : "bg-foreground/80 group-hover:bg-primary"}`} style={{ width: `${pct}%` }}/>
                  </div>
                  <div className="mono text-xs w-14 text-right">{pct}% · {count}</div>
                </button>
              );
            })}
            {filter !== 0 && (
              <button data-testid="filter-clear" onClick={() => setFilter(0)} className="mono text-xs text-primary hover:underline">
                CLEAR FILTER
              </button>
            )}
          </div>
        </div>
      )}

      {/* Reviews list */}
      {reviews.length === 0 ? (
        <div className="text-muted-foreground text-sm">
          {filter ? `No ${filter}-star reviews yet.` : "No reviews yet."}
        </div>
      ) : reviews.map((r) => (
        <div key={r.review_id} data-testid={`review-${r.review_id}`} className="border border-foreground/10 p-5">
          <div className="flex items-center gap-3 mb-3">
            {r.buyer_picture ? (
              <img src={r.buyer_picture} className="w-9 h-9 rounded-full object-cover" alt=""/>
            ) : (
              <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center">{r.buyer_name?.[0]}</div>
            )}
            <div className="flex-1">
              <div className="font-medium">{r.buyer_name}</div>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map((n) => (
                  <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-primary text-primary" : "text-muted"}`}/>
                ))}
              </div>
            </div>
          </div>
          <p className="text-sm text-foreground/80 whitespace-pre-wrap">{r.comment}</p>

          {/* Seller reply */}
          {r.reply && (
            <div data-testid={`review-reply-${r.review_id}`} className="mt-4 border-l-2 border-primary pl-4 py-1 bg-primary/5">
              <div className="overline text-primary mb-1">◆ SELLER REPLY</div>
              <p className="text-sm whitespace-pre-wrap">{r.reply}</p>
            </div>
          )}

          {isSeller && !r.reply && (
            <div className="mt-4">
              {!replyOpen[r.review_id] ? (
                <button
                  data-testid={`open-reply-${r.review_id}`}
                  onClick={() => setReplyOpen((s) => ({ ...s, [r.review_id]: true }))}
                  className="mono text-xs flex items-center gap-1.5 border border-foreground/20 px-3 py-1.5 hover:bg-foreground hover:text-background"
                >
                  <ReplyIcon className="w-3.5 h-3.5"/> REPLY
                </button>
              ) : (
                <div className="space-y-2">
                  <Textarea
                    data-testid={`reply-text-${r.review_id}`}
                    value={replyText[r.review_id] || ""}
                    onChange={(e) => setReplyText((s) => ({ ...s, [r.review_id]: e.target.value }))}
                    placeholder="Thanks for the feedback…"
                    rows={3}
                    className="rounded-none border-foreground/20"
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid={`submit-reply-${r.review_id}`}
                      onClick={() => submitReply(r.review_id)}
                      disabled={!(replyText[r.review_id] || "").trim()}
                      className="bg-primary text-primary-foreground px-4 py-1.5 mono text-xs disabled:opacity-50"
                    >POST REPLY</button>
                    <button
                      onClick={() => setReplyOpen((s) => ({ ...s, [r.review_id]: false }))}
                      className="border border-foreground/20 px-4 py-1.5 mono text-xs"
                    >CANCEL</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
